import React from 'react';

import { classnames } from '@lumapps/classnames';
import { mdiClose } from '@lumapps/lumx/icons';
import { Emphasis, ColorVariant, Theme } from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { sanitizeHTML } from '@lumapps/utils/string/sanitizeHtml';

import { BaseState, BaseStateProps } from '../BaseState';

export interface NoResultsStateProps
    extends Pick<BaseStateProps, 'wrapperProps' | 'title' | 'iconProps' | 'message' | 'messageProps' | 'buttonProps'> {
    /** forwarded class name. */
    className?: string;
    /** searchValue of the input */
    searchValue?: string;
    /** label of the clear button */
    clearButtonLabel?: string;
    /** callback to be executed once the clear button is clicked */
    onClear?(): void;
    /** theme */
    theme?: Theme;
    /** whether this component will be displayed vertically centered or not */
    isVerticallyCentered?: boolean;
}

const allowedTags = ['strong', 'b'];
const CLASSNAME = 'lumx-no-results';

/**
 * No results component
 *
 * @family States
 * @param NoResultsProps
 * @returns NoResults
 */
export const NoResultsState: React.FC<NoResultsStateProps> = ({
    className,
    messageProps,
    message,
    searchValue,
    onClear,
    clearButtonLabel,
    wrapperProps,
    theme,
    title,
    isVerticallyCentered,
    ...props
}) => {
    const { translateKey, translateAndReplace } = useTranslate();

    return (
        <BaseState
            className={classnames(CLASSNAME, className)}
            theme={theme}
            title={title}
            fillSpace={isVerticallyCentered}
            buttonProps={
                onClear
                    ? {
                          emphasis: Emphasis.medium,
                          children: clearButtonLabel || translateKey(GLOBAL.CLEAR),
                          leftIcon: mdiClose,
                          onClick: onClear,
                      }
                    : undefined
            }
            {...props}
            message={message || (!searchValue && !title ? translateKey(GLOBAL.NO_RESULTS) : undefined)}
            messageProps={{
                colorVariant: ColorVariant.N,
                dangerouslySetInnerHTML:
                    searchValue && !message
                        ? {
                              __html: sanitizeHTML(
                                  translateAndReplace(GLOBAL.NO_RESULTS_FOR, {
                                      text: searchValue,
                                  }),
                                  { allowedTags },
                              ),
                          }
                        : undefined,
                ...messageProps,
            }}
            wrapperProps={{
                ...wrapperProps,
                role: 'alert',
            }}
        />
    );
};
